import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import LayoutWithNavbar from './LayoutWithNavbar';
import { fetchForm2BDetailsByAuditLogId } from '../services/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const Form2BAuditDetailsPage = () => {
  const { auditLogId } = useParams(); 
  const [details, setDetails] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 30;

  useEffect(() => {
    const loadDetails = async () => {
      try {
        setLoading(true);

        const res = await fetchForm2BDetailsByAuditLogId(auditLogId);
        console.log("Fetch result:", res);
  
        if (res.success) {
          setDetails(res.data);
        } else {
          alert("Failed to fetch details");
        }
      } catch (error) {

        alert("Network error occurred");
      } finally {
        setLoading(false);
      }
    };
  
    if (auditLogId) loadDetails();
  }, [auditLogId]);
  

  const filteredDetails = details.filter(row => {
    const keyword = searchText.toLowerCase();
    return (
      row.gstin_supplier?.toLowerCase().includes(keyword) ||
      row.trade_name?.toLowerCase().includes(keyword) ||
      row.invoice_number?.toLowerCase().includes(keyword)
    );
  });

  const paginatedDetails = filteredDetails.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalPages = Math.ceil(filteredDetails.length / pageSize);

  const handleExport = () => {
    if (!filteredDetails.length) return alert("No data to export");

    const exportData = filteredDetails.map((row, index) => ({
      'S.No': index + 1,
      'GSTIN': row.gstin_supplier,
      'Trade Name': row.trade_name,
      'Invoice No': row.invoice_number,
      'Invoice Date': row.invoice_date,
      'Invoice Value': row.invoice_value,
      'Taxable Value': row.taxable_value,
      'Central Tax': row.central_tax,
      'State/UT Tax': row.state_ut_tax,
      'Integrated Tax': row.integrated_tax,
      'ITC': row.itc_availability,
      'Reconciled': row.invoice_match_found ? 'Yes' : 'No'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `Form2B-Batch-${auditLogId}`);

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `Form2B_auditlog_Details.xlsx`);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <LayoutWithNavbar>
      <div style={styles.container}>
        <h2 style={styles.heading}>Form 2B Audit Log Details</h2>

        <div style={styles.controls}>
          <input
            type="text"
            placeholder="Search GSTIN, Trade Name or Invoice No"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1); // reset to first page on search
            }}
            style={styles.searchInput}
          />
          <button onClick={handleExport} style={styles.exportButton}>Export to Excel</button>
        </div>

        {loading ? (
          <p style={styles.loading}>Loading...</p>
        ) : (
          <>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['S.No', 'GSTIN', 'Trade Name', 'Invoice No', 'Invoice Date', 'Invoice Value', 'Taxable Value', 'Central Tax', 'State/UT Tax', 'Integrated Tax', 'ITC', 'Reconciled'].map((col, i) => (
                      <th key={i} style={styles.th}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedDetails.map((row, index) => (
                    <tr key={index} style={styles.tr}>
                      <td style={styles.td}>{(currentPage - 1) * pageSize + index + 1}</td>
                      <td style={styles.td}>{row.gstin_supplier}</td>
                      <td style={styles.td}>{row.trade_name}</td>
                      <td style={styles.td}>{row.invoice_number}</td>
                      <td style={styles.td}>{row.invoice_date}</td>
                      <td style={styles.td}>{row.invoice_value}</td>
                      <td style={styles.td}>{row.taxable_value}</td>
                      <td style={styles.td}>{row.central_tax}</td>
                      <td style={styles.td}>{row.state_ut_tax}</td>
                      <td style={styles.td}>{row.integrated_tax}</td>
                      <td style={styles.td}>{row.itc_availability}</td>
                      <td style={styles.td}>{row.invoice_match_found ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={styles.pagination}>
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>⟨ Prev</button>
              <span style={styles.pageInfo}>Page {currentPage} of {totalPages}</span>
              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next ⟩</button>
            </div>
          </>
        )}
      </div>
    </LayoutWithNavbar>
  );
};

const styles = {
  container: { padding: '30px', backgroundColor: '#f8f9fa', minHeight: '100vh' },
  heading: { fontSize: '24px', fontWeight: '600', marginBottom: '20px' },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    gap: '10px',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  searchInput: {
    padding: '10px',
    fontSize: '16px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    width: '35%',
    minWidth: '260px'
  },
  exportButton: {
    padding: '10px 20px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  loading: { fontSize: '18px', textAlign: 'center' },
  tableWrapper: {
    overflowX: 'auto',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 0 10px rgba(0,0,0,0.04)'
  },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '1200px' },
  th: {
    padding: '12px',
    backgroundColor: '#343a40',
    color: '#fff',
    fontWeight: '800',
    textAlign: 'left',
    borderBottom: '1px solid #dee2e6'
  },
  tr: { backgroundColor: '#fff' },
  td: {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
    fontSize: '15px'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '20px',
    gap: '12px'
  },
  pageInfo: { fontWeight: '600' }
};

export default Form2BAuditDetailsPage;
